// the context menu for messages

import { Show } from "solid-js";
import { useApi, useChannels, useMessages } from "@/api";
import { useCtx } from "@/app/context";
import { timeAgo } from "@/atoms/Time.tsx";
import { useCurrentUser } from "@/contexts/currentUser.tsx";
import { useModals } from "@/contexts/modal";
import { useReadTracking } from "@/contexts/read-tracking.tsx";
import { usePermissions } from "@/hooks/usePermissions.ts";
import { Item, Menu, Separator, Submenu } from "./Parts.tsx";

// should i have a separate one for bulk messages?

type MessageMenuProps = {
	channel_id: string;
	message_id: string;
	version_id: string;
};

export function MessageMenu(props: MessageMenuProps) {
	const ctx = useCtx();
	const api2 = useApi();
	const channels2 = useChannels();
	const messagesService = useMessages();
	const readTracking = useReadTracking();
	const message = messagesService.use(
		() => props.channel_id,
		() => props.message_id,
	);
	const channelContext = () => ctx.channel_contexts.get(props.channel_id);
	const [chState, chUpdate] = channelContext() ?? [null, undefined];
	const [, modalCtl] = useModals();
	const { t } = ctx;

	const currentUser = useCurrentUser();
	const self_id = () => currentUser()?.id;
	const channel = channels2.use(() => props.channel_id);
	const { has: hasPermission } = usePermissions(
		self_id,
		() => channel()?.room_id ?? undefined,
		() => props.channel_id,
	);

	const copyId = () => navigator.clipboard.writeText(props.message_id);

	const copyLink = () => {
		const url = new URL(location.origin);
		url.pathname = `/channel/${props.channel_id}/message/${props.message_id}`;
		navigator.clipboard.writeText(url.toString());
	};

	const setReply = () => {
		chUpdate?.("reply_id", props.message_id);
	};

	function markUnread() {
		const r = messagesService._ranges.get(props.channel_id);
		if (!r) return;
		const tl = r.find(props.message_id)?.items;
		if (!tl) return;
		const index = tl.findIndex((i) => i.id === props.message_id && !i.is_local);
		if (index === -1) return;

		const prev = tl[index - 1];
		const ackId = prev?.id ?? tl[index].id;
		readTracking.ack(props.channel_id, ackId, true, false);
	}

	const togglePin = () => {
		if (message()?.pinned) {
			messagesService.unpin(props.channel_id, props.message_id);
		} else {
			messagesService.pin(props.channel_id, props.message_id);
		}
	};

	function deleteMessage() {
		modalCtl.confirm("really delete?", (conf) => {
			if (!conf) return;
			api2.client.http.DELETE(
				"/api/v1/channel/{channel_id}/message/{message_id}",
				{
					params: {
						path: {
							channel_id: props.channel_id,
							message_id: props.message_id,
						},
					},
				},
			);
		});
	}

	const edit = () => {
		chUpdate?.("editingMessage", {
			message_id: props.message_id,
			selection: "end",
		});
	};

	const isSelected = () =>
		chState?.selectedMessages.includes(props.message_id) ?? false;

	const selectMessage = () => {
		if (isSelected()) {
			chUpdate?.("selectedMessages", (prev) =>
				prev.filter((id) => id !== props.message_id),
			);
		} else {
			chUpdate?.("selectMode", true);
			chUpdate?.("selectedMessages", (prev) => [...prev, props.message_id]);
		}
	};

	const logToConsole = () => console.log(JSON.parse(JSON.stringify(message())));

	const addReaction = () => {
		// HACK: open reaction picker next to toolbar button by clicking it
		const button = document.querySelector(
			`[data-message-toolbar="${props.message_id}"] button[title="Add reaction"]`,
		) as HTMLElement | null;
		if (!button) return;
		button.click();
	};

	const viewReactions = () => {
		modalCtl.open({
			type: "view_reactions",
			channel_id: props.channel_id,
			message_id: props.message_id,
		});
	};

	const canAddReaction = () => hasPermission("ReactionAdd");
	const hasReactions = () => {
		const msg = message();
		return msg?.reactions && msg.reactions.length > 0;
	};
	const canReply = () => hasPermission("MessageCreate");
	const canEdit = () => {
		const msg = message();
		return hasPermission("MessageCreate") && msg?.author_id === self_id();
	};
	const canPin = () => hasPermission("MessagePin");
	const canSelect = () =>
		hasPermission("MessageDelete") || hasPermission("MessageRemove");
	const canDelete = () => {
		const msg = message();
		return hasPermission("MessageDelete") || msg?.author_id === self_id();
	};
	const canCreateThread = () => {
		const msg = message();
		const parentChannel = channel();
		// can create threads in text channels, forums, and similar
		const parentThreadable =
			parentChannel &&
			["Text", "Announcement", "Forum", "Forum2"].includes(parentChannel.type);
		return (
			hasPermission("ThreadCreatePublic") && parentThreadable && !msg?.thread
		);
	};

	const createThread = () => {
		modalCtl.prompt("thread name?", (name) => {
			if (!name) return;
			channels2.createThreadFromMessage(props.channel_id, props.message_id, {
				name,
				type: "ThreadPublic",
			});
		});
	};

	const fifteen_mins = 15 * 60 * 1000;
	const three_hours = 3 * 60 * 60 * 1000;
	const eight_hours = 8 * 60 * 60 * 1000;
	const one_day = 24 * 60 * 60 * 1000;
	const one_week = 7 * one_day;

	const remind = (delay?: number) => {
		api2.inbox.createReminder(
			props.channel_id,
			props.message_id,
			delay === undefined ? undefined : new Date(Date.now() + delay),
		);
	};

	return (
		<Menu>
			<Item onClick={markUnread}>mark unread</Item>
			<Item onClick={copyLink}>copy link</Item>
			<Submenu content={t("notifs.remind_me")}>
				<Item onClick={() => remind()}>{t("notifs.bookmark")}</Item>
				<Separator />
				<Item onClick={() => remind(fifteen_mins)}>
					{timeAgo(new Date(Date.now() + fifteen_mins))}
				</Item>
				<Item onClick={() => remind(three_hours)}>
					{timeAgo(new Date(Date.now() + three_hours))}
				</Item>
				<Item onClick={() => remind(eight_hours)}>
					{timeAgo(new Date(Date.now() + eight_hours))}
				</Item>
				<Item onClick={() => remind(one_day)}>
					{timeAgo(new Date(Date.now() + one_day))}
				</Item>
				<Item onClick={() => remind(one_week)}>
					{timeAgo(new Date(Date.now() + one_week))}
				</Item>
			</Submenu>
			<Separator />
			<Show when={canAddReaction()}>
				<Item onClick={addReaction}>add reaction</Item>
			</Show>
			<Show when={hasReactions()}>
				<Item onClick={viewReactions}>view reactions</Item>
			</Show>
			<Show when={canReply()}>
				<Item onClick={setReply}>reply</Item>
			</Show>
			<Show when={canCreateThread()}>
				<Item onClick={createThread}>create thread</Item>
			</Show>
			<Show when={canEdit()}>
				<Item onClick={edit}>edit</Item>
			</Show>
			<Show when={canPin()}>
				<Item onClick={togglePin}>{message()?.pinned ? "unpin" : "pin"}</Item>
			</Show>
			<Show when={canSelect()}>
				<Item onClick={selectMessage}>
					{isSelected() ? "deselect" : "select"}
				</Item>
			</Show>
			<Show when={canDelete()}>
				<Item onClick={deleteMessage} color="danger">
					delete
				</Item>
			</Show>
			<Separator />
			<Item onClick={copyId}>copy id</Item>
			<Item onClick={logToConsole}>log to console</Item>
		</Menu>
	);
}
